import { Trans } from "@lingui/react/macro";
import { ChatMarkdown } from "@rakazo/chat-ui/web";
import { Button } from "@rakazo/ui-web";
import { useLayoutEffect, useRef, useState } from "react";
import { observeResize, onViewportResize } from "../../lib/shared-resize";
import { useMessageArticle } from "./message-article";

/** Collapse only beyond 1.5 visible chat screens. / Згортати лише понад 1,5 видимих екрана чату. */
export const MESSAGE_COLLAPSE_VIEWPORTS = 1.5;

/**
 * Long assistant text stays readable in the chat without pushing the next messages away: the
 * bubble shows the opening and a fade, and the full text opens in a reader above the composer.
 *
 * The article belongs to the stable conversation-level host, not this row's lifetime.
 * Стаття належить сталому вузлу розмови, а не життєвому циклу цього рядка.
 */
export function CollapsibleMessageText({
  text,
  streaming = false,
}: {
  text: string;
  streaming?: boolean;
}) {
  const openArticle = useMessageArticle();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [long, setLong] = useState(false);
  const [previewHeight, setPreviewHeight] = useState(0);

  useLayoutEffect(() => {
    if (streaming) {
      setLong(false);
      return;
    }
    const content = contentRef.current;
    if (!content) return;
    const viewport = content.closest<HTMLElement>('[data-testid="transcript"]');
    const visualViewport = window.visualViewport;
    const measure = () => {
      const style = viewport ? getComputedStyle(viewport) : null;
      const height = viewport
        ? viewport.clientHeight -
          (Number.parseFloat(style!.paddingTop) || 0) -
          (Number.parseFloat(style!.paddingBottom) || 0)
        : (visualViewport?.height ?? window.innerHeight);
      setPreviewHeight(Math.max(0, height));
      setLong(height > 0 && content.scrollHeight > MESSAGE_COLLAPSE_VIEWPORTS * height);
    };
    // Every block in the transcript shares one observer and one window listener.
    // Усі блоки чату ділять один спостерігач і один слухач вікна.
    const stopContent = observeResize(content, measure);
    const stopViewport = viewport ? observeResize(viewport, measure) : undefined;
    const stopWindow = onViewportResize(measure);
    measure();
    return () => {
      stopContent();
      stopViewport?.();
      stopWindow();
    };
  }, [streaming, text]);

  // The fade ends at the clip edge, so the control below sits on a solid background and never
  // reads as part of the text. / Згасання закінчується на краю обрізання, кнопка стоїть окремо.
  return (
    <>
      <div
        className={long ? "relative overflow-hidden" : undefined}
        style={{ maxHeight: long ? `min(22rem, ${previewHeight}px)` : undefined }}
      >
        <div ref={contentRef} data-collapsible-message-content>
          <ChatMarkdown streaming={streaming}>{text}</ChatMarkdown>
        </div>
        {long ? (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-muted via-muted/85 to-transparent"
          />
        ) : null}
      </div>
      {long ? (
        <div className="mt-2 flex justify-center">
          <Button
            ref={triggerRef}
            variant="outline"
            onClick={() => openArticle(text, triggerRef.current)}
            className="h-11 rounded-full bg-background px-5 text-foreground shadow-xs md:h-9"
          >
            <Trans>Show more</Trans>
          </Button>
        </div>
      ) : null}
    </>
  );
}
